import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { formatTimestamp } from '../../../utils/formatTimestamp';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {useLocation, useNavigate, useSearchParams} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import {getUsersByIds} from '../../../firebase/firestore/users';
import PersonCell, {indexUsersById} from '../components/PersonCell';
import {collectionAssigneeChatParentId} from '../../../firebase/firestore/chats';
import {listCollectionTasks} from '../collectionTaskApi';
import {
    CT_STATUSES,
    collectionTaskPath,
    hasAnyRole,
    CM_ROLES,
    statusColor,
    statusLabel,
    priorityColor,
} from '../collectionTaskPayload';

export default function ListCollectionTasks() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {role, roles} = useSession();
    const canManage = hasAnyRole(role, roles, CM_ROLES);
    const isCollector = hasAnyRole(role, roles, ['collector']) && !canManage;

    const [items, setItems] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Hydrate filters from the URL so CT detail Back (returnTo) restores them.
    const [q, setQ] = useState(() => searchParams.get('q') || '');
    const [status, setStatus] = useState(() => searchParams.get('status') || '');

    useEffect(() => {
        const next = new URLSearchParams();
        if (q) next.set('q', q);
        if (status) next.set('status', status);
        const nextStr = next.toString();
        const curStr = searchParams.toString();
        if (nextStr !== curStr) {
            setSearchParams(next, {replace: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, status]);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Collectors: omit assigneeId so API returns own + unassigned claimable tasks.
            const list = await listCollectionTasks({
                q: q || undefined,
                status: status || undefined,
            });
            setItems(list);
            const ids = [...new Set(list.map((r) => r.assigneeId).filter(Boolean))];
            if (ids.length) {
                setPeople(indexUsersById(await getUsersByIds(ids)));
            } else {
                setPeople({});
            }
        } catch (e) {
            setError(e?.message || 'Failed to load tasks');
            setItems([]);
            setPeople({});
        } finally {
            setLoading(false);
        }
    }, [q, status]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filtered = useMemo(() => items, [items]);

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h5">Collection Tasks</Typography>
                    {isCollector && (
                        <Typography variant="body2" color="text.secondary">
                            Your tasks and unassigned work you can claim.
                        </Typography>
                    )}
                </Box>
                {canManage && (
                    <Button variant="contained" onClick={() => navigate('/collection/tasks/create')}>
                        Create Task
                    </Button>
                )}
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} mb={2}>
                <TextField
                    size="small"
                    label="Search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    sx={{minWidth: 240}}
                />
                <FormControl size="small" sx={{minWidth: 180}}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {CT_STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={reload}>Refresh</Button>
            </Stack>

            {loading && (
                <Stack alignItems="center" py={4}><CircularProgress/></Stack>
            )}
            {error && <Typography color="error">{error}</Typography>}

            {!loading && !error && (
                <Paper variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Tooltip title="Collection Task number" arrow placement="top">
                                        <span>ID</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{width: '28%'}}>Title</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Priority</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Classification</TableCell>
                                <TableCell>Assignee</TableCell>
                                <TableCell>Updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography color="text.secondary" py={2} align="center">
                                            No collection tasks found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{cursor: 'pointer'}}
                                    onClick={() => navigate(collectionTaskPath(row), {
                                        state: {
                                            returnTo: `${location.pathname}${location.search || ''}`,
                                        },
                                    })}
                                >
                                    <TableCell sx={{fontFamily: 'monospace', whiteSpace: 'nowrap'}}>
                                        {row.ctNumber}
                                    </TableCell>
                                    <TableCell sx={{
                                        maxWidth: 280,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {row.title}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel(row.displayStatus || row.status)}
                                            color={statusColor(row.displayStatus || row.status)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{whiteSpace: 'nowrap'}}>
                                        {row.priority && (
                                            <Chip
                                                size="small"
                                                label={row.priority}
                                                color={priorityColor(row.priority)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{whiteSpace: 'nowrap'}}>
                                        {row.classification && (
                                            <Chip
                                                size="small"
                                                label={row.classification}
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{maxWidth: 220}}
                                    >
                                        {row.assigneeId ? (
                                            <PersonCell
                                                userId={row.assigneeId}
                                                profile={people[row.assigneeId]}
                                                chatParentId={collectionAssigneeChatParentId(row.assigneeId)}
                                            />
                                        ) : (
                                            <Chip size="small" label="Unassigned" variant="outlined"/>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatTimestamp(row.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
